// =============================================================================
// supplier-monitoring-client.ts — Client API de la Section « Suivi fournisseurs » (BC analytics), DOUBLABLE.
// =============================================================================
// V-SUIVI-FOURN-read (PFP-2026-07-21-002). Miroir de l'endpoint ADMIN
// GET /api/admin/campaigns/:campaignId/supplier-monitoring (AdminSessionGuard, withAdmin). Comme les autres
// clients UI, expose une INTERFACE consommée par un hook TanStack ; l'impl HTTP réelle (fetch) est
// substituable via le DI seam (SupplierMonitoringClientProvider) sans toucher l'UI (stub-first).
//
// AUTORITÉ = API : la composition des 4 BC (contact référent, dernière activité read-time, état CGU, taux de
// complétion, cohorte/historique), le badge « connecté sans saisie >48h » et l'agrégat SIREN « Complet » sont
// calculés AU VOL côté serveur (read-model analytics, composition EN MÉMOIRE via ports ACL — jamais de JOIN
// cross-schéma). L'UI LIT et habille (libellé/transparence WCAG). Le campaignId est TOUJOURS la ressource d'URL.
//
// DTO = miroirs HTTP (PAS le Published Language, PAS @jeko/db) : les enums d'état sont répliqués en unions de
// littéraux ASCII (parité Prisma) — la SPA ne couple pas au paquet DB.

import { HttpApiError } from '@/api/http-error';

/** État d'établissement (parité Prisma EstablishmentState, ASCII). */
export type EstablishmentStateDto =
  | 'Brouillon'
  | 'Soumis'
  | 'Refuse'
  | 'Actif'
  | 'Suspendu'
  | 'Archive';

/** État de participation (parité Prisma ParticipationState, ASCII). */
export type ParticipationStateDto =
  | 'Invite'
  | 'EnSaisie'
  | 'Soumis'
  | 'Verrouille'
  | 'ReouvertNego';

/** Contact référent d'une ligne (PII admin). `telephone` = marqueur différé (colonne absente back). */
export interface SupplierMonitoringReferentDto {
  readonly nomComplet: string | null;
  readonly email: string | null;
  readonly telephone: string | null;
}

/** Une ligne de la table analytique « Suivi fournisseurs » (grain SIRET participant). Miroir HTTP. */
export interface SupplierMonitoringRowDto {
  readonly supplierSiret: string;
  readonly siren: string;
  readonly raisonSociale: string | null;
  readonly etablissementState: EstablishmentStateDto | null;
  readonly referent: SupplierMonitoringReferentDto | null;
  /** Dernière connexion (login) dérivée read-time (ISO 8601) ; null si jamais connecté / cross-tenant. */
  readonly lastActivityAt: string | null;
  readonly cguSigned: boolean;
  readonly submitted: boolean;
  readonly respondedCoupleCount: number;
  readonly perimeterCoupleCount: number;
  /** Taux de complétion ∈ [0,1] ; null si périmètre vide. */
  readonly completionPct: number | null;
  /** Badge « connecté sans saisie >48h » (croisement last-activity × non-soumis côté API). */
  readonly connectedNoSubmissionOver48h: boolean;
  readonly participationState: ParticipationStateDto;
  readonly invitedAt: string | null;
  readonly submittedAt: string | null;
  readonly lastReminderAt: string | null;
}

/** Agrégat en vue SIREN (« Complet » = tous les SIRET participants du SIREN ont soumis). Miroir HTTP. */
export interface SupplierMonitoringSirenAggregateDto {
  readonly siren: string;
  readonly raisonSociale: string | null;
  readonly siretCount: number;
  readonly submittedSiretCount: number;
  readonly complet: boolean;
}

/**
 * Analyse comportementale de la cohorte invitée (DBU §2.5 l.1071 — volet Focus). Deux compteurs DISJOINTS :
 * invités jamais connectés vs connectés sans saisie (SANS seuil >48h, ≠ badge par ligne). Miroir HTTP.
 */
export interface SupplierBehavioralSummaryDto {
  readonly invitedSupplierCount: number;
  readonly invitedNotConnectedCount: number;
  readonly connectedNoSubmissionCount: number;
}

/** Réponse de la Section « Suivi fournisseurs » : vue SIRET (rows) + vue SIREN (agrégats). Miroir HTTP. */
export interface CampaignSupplierMonitoringDto {
  readonly campaignId: string;
  readonly perimeterCoupleCount: number;
  readonly generatedAt: string;
  /**
   * Numéro du cycle actuellement OUVERT (collecte en cours) ; `null` si aucun cycle ouvert. VRAI signal
   * d'ouverture de campagne (champ dédié, PAS l'écho d'un filtre) : cible ET condition d'activation de la
   * relance manuelle admin (A-16). L'écran de suivi gate ses boutons de relance dessus.
   */
  readonly openCycleNumber: number | null;
  readonly rows: readonly SupplierMonitoringRowDto[];
  readonly sirenAggregates: readonly SupplierMonitoringSirenAggregateDto[];
  /** Analyse comportementale (invités non connectés vs connectés sans saisie) — DBU l.1071. */
  readonly behavioralSummary: SupplierBehavioralSummaryDto;
}

/** Erreur du client Suivi fournisseurs (conserve le statut HTTP — intercepteur 401 admin). */
export class SupplierMonitoringApiError extends HttpApiError {
  public constructor(message: string, status?: number) {
    super(message, status, 'SupplierMonitoringApiError');
  }
}

/** Interface DOUBLABLE : une seule lecture admin (aucune mutation — invariant n°1). */
export interface SupplierMonitoringClient {
  getSupplierMonitoring(campaignId: string): Promise<CampaignSupplierMonitoringDto>;
}
